import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelopeOpen, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { IoNotifications } from "react-icons/io5";
import Router from "..";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

export const Alarm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const alarmRef = useRef(null);
  const [alarmList, setAlarmList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const isLastPage = alarmList.length >= total;
  const history = useHistory();

  // 무한스크롤
  const observerRef = useInfiniteScroll(
    () => setPage((prev) => prev + 1),
    loading,
    isLastPage
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        alarmRef.current &&
        !alarmRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    fetchAlarmList(page);
  }, [page]);

  const fetchAlarmList = async (page) => {
    setLoading(true);
    const data = await Router.Api.getAlarmList({ page });

    if (page === 1) {
      setAlarmList(data?.result ?? []);
    } else {
      setAlarmList((prev) => [...prev, ...(data?.result ?? [])]);
    }

    setTotal(data?.total ?? 0);
    setUnread(data?.unread ?? 0);
    setLoading(false);
  };

  const displayCount = unread > 99 ? "99+" : unread;

  const handleReadAlarm = async (id) => {
    const result = await Router.Api.readAlarm(id);
    if (result) {
      setAlarmList((prev) =>
        prev.map((alarm) =>
          alarm.id === id ? { ...alarm, read_status: true } : alarm
        )
      );
      setUnread((prev) => prev - 1);
    }
  };

  const handleReadAllAlarm = async () => {
    const result = await Router.Api.readAllAlarm();
    if (result) {
      setAlarmList((prev) =>
        prev.map((alarm) => ({ ...alarm, read_status: true }))
      );
      setUnread(0);
    }
  };

  const handleAlarmClick = async (alarm) => {
    await handleReadAlarm(alarm.id);

    if (alarm.campaign_id) {
      setIsOpen(false);
      history.push(`/campaign/search?campaign_id=${alarm.campaign_id}`);
    }
  };

  return (
    <div ref={alarmRef} className="relative">
      {/* 알림 버튼 */}
      <button
        className="flex items-center justify-center p-0 border-0 cursor-pointer transition-all duration-200 ease-in-out rounded-full relative"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <IoNotifications className="w-8 h-8 md:w-10 md:h-10 text-white" />
        {unread > 0 && (
          <div
            className={`absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 z-[1] bg-red-500 text-white min-w-[12px] h-[12px] md:min-w-[16px] md:h-[16px] ${
              unread > 99
                ? "px-0.5 py-0 text-[5px] md:px-1.5 md:py-0.5 md:text-[7px]"
                : "px-0.5 py-0 text-[6px] md:px-0.5 md:py-0.5 md:text-[8px]"
            } font-bold rounded-full md:text-base flex items-center justify-center shadow-[0_2px_4px_rgba(0,0,0,0.2)]`}
          >
            {displayCount}
          </div>
        )}
      </button>

      {/* 알림 목록 */}
      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute top-full mt-1.5 z-[1000] bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)] h-[400px] max-h-[500px] overflow-hidden flex flex-col w-[260px] min-[360px]:w-[290px] md:w-[320px] md:max-w-[320px] right-2 min-[360px]:right-0"
        >
          <div
            className={`h-full p-2 ${
              alarmList.length === 0 ? "overflow-hidden" : "overflow-y-auto"
            }`}
          >
            {alarmList.length === 0 ? (
              <div className="py-2 text-black text-[14px] text-center flex justify-center items-center h-full">
                알람 없음
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center px-3 py-3 mb-2.5">
                  <div className="text-[16px] font-bold text-black">알림</div>
                  <button
                    className="text-[12px] text-black cursor-pointer px-1.5 py-0.5 rounded border border-[#ececec] transition-colors hover:bg-[#f0f0f0]"
                    onClick={handleReadAllAlarm}
                  >
                    모두 읽음
                  </button>
                </div>
                {alarmList.map((alarm, index) => (
                  <div key={alarm.id}>
                    <div className="px-3 py-3 rounded-[10px] cursor-pointer flex justify-between items-center transition-colors hover:bg-[#f8f8f8]">
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => handleAlarmClick(alarm)}
                      >
                        <div
                          className={`font-medium ${
                            alarm.read_status ? "text-[#999]" : "text-black"
                          }`}
                        >
                          {alarm.title}
                        </div>
                        <div
                          className={`text-[12px] mt-1 ${
                            alarm.read_status ? "text-[#999]" : "text-black"
                          }`}
                        >
                          {alarm.description}
                        </div>
                        <div className="text-[11px] text-[#999] mt-1">
                          {alarm.created_at}
                        </div>
                      </div>
                      <button
                        className={`flex-shrink-0 mt-0.5 cursor-pointer text-[14px] ${
                          alarm.read_status ? "text-[#999]" : "text-[#4e4e4e]"
                        }`}
                        onClick={() => handleReadAlarm(alarm.id)}
                      >
                        <FontAwesomeIcon
                          icon={alarm.read_status ? faEnvelopeOpen : faEnvelope}
                        />
                      </button>
                    </div>
                    {index < alarmList.length - 1 && (
                      <div className="h-[1px] bg-[#eee] mx-3" />
                    )}
                  </div>
                ))}
              </div>
            )}
            <div ref={observerRef} className="h-[5px]"></div>
          </div>
        </div>
      )}
    </div>
  );
};
